/** Rolling windows on server `receivedDate` (when mail was stored). */
export const ROLLING_RECEIVED_DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '1w', label: 'Last Week' },
  { id: '30d', label: 'Last 30 days' },
  { id: '60d', label: 'Last 60 days' },
  { id: '3m', label: 'Last 3 months' },
  { id: '6m', label: 'Last 6 Months' },
  { id: '9m', label: 'Last 9 months' },
  { id: '1y', label: 'Last year' },
] as const;

export type ReceivedDatePreset = (typeof ROLLING_RECEIVED_DATE_PRESETS)[number]['id'];

export type SearchFolderPeriodPreset = 'all' | ReceivedDatePreset;

export const ROLLING_PRESET_IDS: readonly ReceivedDatePreset[] = ROLLING_RECEIVED_DATE_PRESETS.map(
  (p) => p.id,
);

export function isReceivedDatePreset(value: string): value is ReceivedDatePreset {
  return (ROLLING_PRESET_IDS as readonly string[]).includes(value);
}

/** `to` is slightly after now so API `lt` includes the current instant. */
export function receivedDateRangeForPreset(preset: ReceivedDatePreset): { from: Date; to: Date } {
  const to = new Date(Date.now() + 1000);
  const from = new Date();
  switch (preset) {
    case 'today':
      from.setUTCHours(0, 0, 0, 0);
      break;
    case '1w':
      from.setUTCDate(from.getUTCDate() - 7);
      break;
    case '30d':
      from.setUTCDate(from.getUTCDate() - 30);
      break;
    case '60d':
      from.setUTCDate(from.getUTCDate() - 60);
      break;
    case '3m':
      from.setUTCMonth(from.getUTCMonth() - 3);
      break;
    case '6m':
      from.setUTCMonth(from.getUTCMonth() - 6);
      break;
    case '9m':
      from.setUTCMonth(from.getUTCMonth() - 9);
      break;
    case '1y':
      from.setUTCFullYear(from.getUTCFullYear() - 1);
      break;
    default:
      from.setUTCDate(from.getUTCDate() - 30);
  }
  return { from, to };
}

export function receivedDatePresetLabel(preset: ReceivedDatePreset): string {
  return ROLLING_RECEIVED_DATE_PRESETS.find((p) => p.id === preset)?.label ?? preset;
}

export function searchFolderPeriodPresetLabel(
  periodPreset?: string | null,
  periodYear?: number | null,
): string | null {
  if (periodYear != null && Number.isFinite(periodYear)) {
    return `Messages from ${periodYear}`;
  }
  const preset = (periodPreset ?? '').trim().toLowerCase();
  if (preset === 'all' || preset === '') return null;
  if (isReceivedDatePreset(preset)) {
    return `Messages from ${receivedDatePresetLabel(preset).toLowerCase()}`;
  }
  return null;
}

/** Compact label for sidebar rows and filter bar (e.g. "Last 30 days", "2025"). */
export function searchFolderPeriodShortLabel(
  periodPreset?: string | null,
  periodYear?: number | null,
): string | null {
  if (periodYear != null && Number.isFinite(periodYear)) {
    return String(periodYear);
  }
  const preset = (periodPreset ?? '').trim().toLowerCase();
  if (preset === 'all' || preset === '') return null;
  if (isReceivedDatePreset(preset)) {
    return receivedDatePresetLabel(preset);
  }
  return null;
}
